<script setup lang="ts">
import { ref, Ref, watch, computed } from "vue";
import { useRoute } from "vue-router";
import { useHead } from "@unhead/vue";

import router from "../router";
import beacon from "../lib/beacon";
import { useAuth } from "../lib/useAuth";
import { useTheme } from "../lib/useTheme";
import { useRfp, useRfpLocations, useRfpDisplayProperties } from "@/lib/useRfp";
import { useProposalFormConfig } from "@/lib/useProposalFormConfig";
import { usePolicyProject } from "@/lib/usePolicy";
import { createProposal, useRfpProposals } from "@/lib/useProposal";

import MapCanvas from "@/components/MapCanvas.vue";
import MemoryCard from "@/components/MemoryCard.vue";
import RfpLocationDataCard from "@/components/RfpLocationDataCard.vue";
import RfpLocationTile from "@/components/RfpLocationTile.vue";
import RfpQuestions from "@/components/RfpQuestions.vue";
import RfpSummaryCard from "@/components/RfpSummaryCard.vue";
import RfpTimeline from "@/components/RfpTimeline.vue";
import RfpTimelineChip from "@/components/RfpTimelineChip.vue";
import RfpContentTabs from "@/components/RfpContentTabs.vue";

const FAQ_LINK = "https://sta.link/marketplace-faqs";

const props = defineProps<{ rfpId: string }>();

const reactiveRfpId = computed(() => props.rfpId);
beacon.rfpView(props.rfpId);

const { providerId, isProvider, isAuthed } = useAuth();
const { dark } = useTheme();
const {
  rfp,
  loading: rfpLoading,
  reloadRfp,
  error: rfpFetchError,
  projectCount,
  requestType,
} = useRfp(reactiveRfpId);

/* What can the user do with this RFP? */
const { canAccessQuestions, canAccessProjectContent, canEditProjectPhases } =
  usePolicyProject(rfp);

// This makes it so subsequent composables won't run if the user cannot access project content
const rfpIdIfCanAccessProjectContent = computed(() =>
  canAccessProjectContent.value ? reactiveRfpId.value : undefined,
);
const rfpIdIfAuthed = computed(() =>
  isAuthed.value ? reactiveRfpId.value : undefined,
);
const { locations } = useRfpLocations(rfpIdIfCanAccessProjectContent);

const { project, rfpSummaryCardItems, renderedIntroStatement, displayGoals } =
  useRfpDisplayProperties(rfp);

const {
  hasParts,
  createParts,
  loading: partsLoading,
} = useProposalFormConfig(rfpIdIfAuthed);

// TODO: Make this work when migrating to migrating to @unhead/vue - see #957
/*
const metaInfo = () => {
  return {
    title: rfp.value ? `${requestType}: ${rfp.value.name}` : "",
  };
};
*/

const route = useRoute();
const activeTab: Ref<string> = ref(route.hash?.substring(1) || "timeline");
watch(
  () => route.hash,
  () => {
    activeTab.value = route.hash?.substring(1) || "timeline";
  },
);
function switchTabs(tab: string) {
  router.replace({ hash: `#${tab}` });
}

const numVisibleRfpLocations = ref(10);

const visibleRfpLocations = computed(() => {
  if (!locations.value) return [];
  return locations.value.slice(0, numVisibleRfpLocations.value);
});

function loadMoreLocations() {
  beacon.rfpLoadMoreLocations(props.rfpId, numVisibleRfpLocations.value);
  numVisibleRfpLocations.value = numVisibleRfpLocations.value + 10;
}

async function openDisclosures() {
  beacon.rfpDisclosuresOpen(props.rfpId);
  if (rfp.value) {
    window.open(rfp.value.disclosures_link, "_blank");
  }
}

/*
 * Proposal state and functions
 */

const maybeRfpId = ref(props.rfpId);

// This is a little hacky, but it is so useRfpProposals only runs if the user is a provider, so they can get their own proposals
// Note: without this, if a user a is a buyer that owns this RFP, the API will send back all the proposals for this RFP
const conditionalRfpIdToGetProposals = computed(() => {
  if (isProvider.value) {
    return maybeRfpId.value;
  } else {
    return undefined;
  }
});

// Set submitted_only = false to get all proposals, and include_test_proposals = true
// so as not to lock out Station A team's test proposals.
const {
  proposals,
  loading: proposalsLoading,
  reload: reloadProposals,
} = useRfpProposals(conditionalRfpIdToGetProposals, providerId, false, true);

const proposal = computed(() =>
  proposals.value.length > 0 ? proposals.value[0] : undefined,
);

async function startProposal() {
  if (!proposal.value) {
    beacon.proposalCreate(providerId.value, props.rfpId);
    await createProposal(props.rfpId);
    await reloadProposals();
  }
  goToProposal();
}
function goToProposal() {
  if (proposal.value) {
    router.push({
      name: "proposal",
      params: { proposalId: proposal.value.id },
    });
  }
}

function goToProposalPreview() {
  beacon.rfpPreviewFormView(props.rfpId);
  // TODO: Fix
  router.push({
    name: "proposal-preview",
    query: {
      rfp_id: props.rfpId,
    },
  });
}

watch(activeTab, (newContent, oldContent) => {
  beacon.rfpSetView(props.rfpId, oldContent, newContent);
});

watch(
  rfpFetchError,
  () => {
    if (rfpFetchError.value) {
      router.push({ name: "not-found" });
    }
  },
  { immediate: true },
);

const anythingLoading = computed(() => {
  if (isAuthed.value) {
    return rfpLoading.value || proposalsLoading.value || partsLoading.value;
  } else {
    return rfpLoading.value;
  }
});

useHead({
  title: () => (rfp.value?.name ? `Project: ${rfp.value.name}` : "Project"),
});
</script>

<template>
  <div>
    <div class="tw:z-10 tw:flex tw:space-x-2 tw:fixed tw:right-6 tw:bottom-6">
      <RouterLink
        v-if="canEditProjectPhases"
        :to="{ name: 'rfp-builder', params: { rfpId: props.rfpId } }"
      >
        <t-big-pill-btn
          class="tw:border-success-700 tw:dark:border-success-600 tw:bg-success-300 tw:dark:bg-success-900 tw:dark:hover:bg-success-700 tw:hover:border-success-500 tw:dark:hover:border-success-500 tw:hover:bg-success-200 tw:active:bg-success-100 tw:active:border-success-400 tw:dark:active:bg-success-600 tw:dark:active:border-success-400"
        >
          <t-icon left class="tw:text-success-700! tw:dark:text-success-300!">
            settings
          </t-icon>
          <span class="tw:ml-2 tw:text-success-700 tw:dark:text-success-300">
            Edit RFP
          </span>
        </t-big-pill-btn>
      </RouterLink>
    </div>
    <div v-if="rfpLoading" key="loading" class="tw:w-full">
      <t-skeleton kind="block" class="tw:p-32 tw:w-full" />
      <div class="tw:p-5 tw:grid tw:gap-3">
        <t-skeleton kind="card" class="tw:mt-8 tw:py-8 tw:w-full" />
        <t-skeleton kind="card" class="tw:py-4 tw:w-full" />
        <t-skeleton kind="block" class="tw:p-64 tw:w-full" />
      </div>
    </div>
    <div v-else key="content" class="tw:w-full">
      <div class="tw:w-full tw:h-[150px] tw:sm:h-[250px]">
        <map-canvas
          v-if="project"
          :geojson="project.geo"
          :fill="dark ? '#111' : '#eee'"
          fill-opacity="0.7"
          :stroke="dark ? '#888' : '#ccc'"
          stroke-width="1"
        />
      </div>
      <t-container>
        <t-card-title class="tw:px-0 tw:py-3">
          <div v-if="rfp" class="tw:flex tw:flex-col">
            <div class="tw:flex tw:flex-wrap tw:gap-4 tw:mb-4">
              <t-chip dense outlined color="red" v-if="!rfp.is_live">
                Not Live
              </t-chip>
              <t-chip dense outlined color="gray" v-if="project">
                <t-icon small left>location_on</t-icon>
                <span v-if="projectCount === 1">
                  {{ project.address.locality }}, {{ project.address.region }}
                </span>
                <span v-else>
                  {{ project.address.locality }} Across
                  {{ project.address.region }}
                </span></t-chip
              >
              <rfp-timeline-chip
                :rfp-id="reactiveRfpId"
                :proposal-shortlisted="proposal ? proposal.shortlisted : false"
              />
            </div>
            <h1 class="tw:text-xl sm:tw:sta-title tw:font-semibold tw:pb-1">
              {{ rfp.name }}
            </h1>
          </div>
        </t-card-title>
        <div
          class="tw:text-lg tw:font-thin tw:mb-12"
          v-html="renderedIntroStatement"
        />
        <rfp-content-tabs
          class="tw:mb-24"
          :restricted="!canAccessProjectContent"
          :value="activeTab"
          @user-input="switchTabs"
          v-if="rfp"
        >
          <template #timeline>
            <div class="tw:pb-4">
              <p class="tw:font-semibold tw:text-base tw:text-zinc-400">
                Review {{ requestType }} progress and timeline
              </p>
              <span class="tw:text-zinc-500"
                >Browse project milestones and take relevant action.</span
              >
            </div>
            <rfp-timeline
              :request-type="requestType"
              :rfp="rfp"
              :proposal="proposal"
              :props-loading="anythingLoading"
              :has-parts="hasParts ? hasParts : false"
              @open-disclosures="activeTab = 'disclosures'"
              @open-questions="activeTab = 'questions-answers'"
              @start-proposal="startProposal"
              @open-proposal="goToProposal"
              @preview-proposal="goToProposalPreview"
              @create-parts="createParts"
            />
          </template>
          <template #disclosures>
            <div v-if="canAccessProjectContent">
              <p class="tw:font-semibold tw:text-base tw:text-zinc-400">
                Review {{ requestType }} summary
              </p>
              <span class="tw:text-zinc-500"
                >Browse project details and dive deeper into the disclosures
                folder.</span
              >
              <div class="tw:grid tw:gap-4">
                <div class="tw:flex tw:justify-start tw:mt-4">
                  <TBtn
                    :disabled="!rfp.disclosures_link"
                    :color="rfp.disclosures_link ? 'green' : 'gray'"
                    @click="openDisclosures"
                  >
                    Open disclosures folder
                    <t-icon right>open_in_new</t-icon>
                  </TBtn>
                </div>
                <rfp-summary-card :items="rfpSummaryCardItems" />
                <t-card v-if="displayGoals && displayGoals.length > 0">
                  <t-card-title>
                    <t-icon left>flag</t-icon>
                    <h2 class="tw:text-xl">Goals</h2>
                  </t-card-title>
                  <t-card-text class="tw:pt-0 tw:text-base">
                    <ul>
                      <li
                        v-for="(g, i) of displayGoals"
                        :key="i"
                        class="tw:my-2"
                        v-html="g"
                      />
                    </ul>
                  </t-card-text>
                </t-card>
                <t-card
                  v-if="
                    !!project &&
                    project.providerQualifications &&
                    project.providerQualifications.length > 0
                  "
                >
                  <t-card-title>
                    <t-icon left>how_to_reg</t-icon>
                    <h2 class="tw:text-xl">Provider qualifications</h2>
                  </t-card-title>
                  <t-card-text class="tw:pt-0 tw:text-base">
                    <ul>
                      <li
                        v-for="(q, i) of project.providerQualifications"
                        :key="i"
                        class="tw:my-2"
                        v-html="q"
                      />
                    </ul>
                  </t-card-text>
                </t-card>
                <t-card
                  v-if="
                    !!project &&
                    project.evaluationCriteria &&
                    project.evaluationCriteria.length > 1
                  "
                >
                  <t-card-title>
                    <t-icon left>star_half</t-icon>
                    <h2 class="tw:text-xl">Criteria</h2>
                  </t-card-title>
                  <t-card-text class="tw:pt-0 tw:text-base">
                    <ul>
                      <li
                        v-for="(c, i) of project.evaluationCriteria"
                        :key="i"
                        class="tw:my-2"
                        v-html="c"
                      />
                    </ul>
                  </t-card-text>
                </t-card>
              </div>
              <p
                class="tw:font-semibold tw:text-base tw:text-zinc-400 tw:mt-10"
              >
                Review {{ requestType }} location data
              </p>
              <span class="tw:text-zinc-500"
                >Browse project location details and dive deeper with the
                disclosures folder.</span
              >
              <div v-if="projectCount && projectCount > 1" class="tw:my-4">
                <rfp-location-tile
                  v-for="location in visibleRfpLocations"
                  :key="location.data.id"
                  :location="location"
                  class="tw:mb-3"
                  :rfp-id="rfpId"
                />
                <div
                  class="tw:flex tw:justify-center"
                  v-if="numVisibleRfpLocations <= projectCount"
                >
                  <TBtn flat @click="loadMoreLocations">
                    <t-icon left>expand_more</t-icon>
                    Load more locations
                  </TBtn>
                </div>
              </div>
              <rfp-location-data-card
                v-else-if="locations && locations.length > 0"
                :location="locations[0]"
                class="tw:my-4"
              />
            </div>
          </template>
          <template #questions>
            <rfp-questions
              :rfp-id="rfpId"
              :enable-question-button="canAccessQuestions"
              @reload-rfp="reloadRfp"
              v-if="canAccessProjectContent"
            />
            <t-card-text
              class="tw:text-zinc-500 tw:text-xs tw:text-center tw:mt-8"
            >
              For answers to common questions about the Station A Marketplace,
              visit our <a :href="FAQ_LINK">Marketplace FAQs</a>
            </t-card-text>
          </template>
        </rfp-content-tabs>
      </t-container>
      <memory-card
        storage-suffix="rfp"
        version="1"
        icon="lock_open"
        title="Track and manage your RFPs in one place"
      >
        Review a project's timeline, browse details, submit questions, see
        responses, and track {{ requestType }} progress through a simple and
        transparent interface.
      </memory-card>
    </div>
  </div>
</template>
